/** Human-readable display names for entity types */
export const ENTITY_DISPLAY_NAMES: Record<string, string> = {
  // Core entities
  employee: 'Employee',
  education: 'Education',
  experience: 'Experience',
  org_assignment: 'Organization Assignment',
  customer_employee: 'Employee Record',
  role: 'Role',
  user_role_assignment: 'Role Assignment',
  role_permission: 'Permission',
  scope_instance: 'Organization Unit',
  logical_scope: 'Organization Level',
  account_settings: 'Account Settings',
  account_access_method: 'Access Method',
  impersonation_session: 'Impersonation Session',
  admin_action: 'Admin Action',
  module_access: 'Module Access',
  // Review Cloud entities
  cycle: 'Review Cycle',
  review: 'Review',
  goal: 'Goal',
  idp: 'Development Plan',
  idp_goal: 'Development Goal',
  idp_comment: 'Development Comment',
  idp_attachment: 'Development Attachment',
  goal_cycle: 'Goal Cycle',
  goal_comment: 'Goal Comment',
  goal_file: 'Goal File',
  goal_category: 'Goal Category',
  review_file: 'Review File',
  review_approval: 'Review Approval',
  review_acknowledgement: 'Review Acknowledgement',
  cycle_participant: 'Cycle Participant',
  flowboard_user: 'Review Participant',
  grid_cycle: 'Talent Grid Cycle',
  grid_score: 'Talent Grid Score',
  box_template: 'Box Template',
  calibration_session: 'Calibration Session',
  calibration_participant: 'Calibration Participant',
  calibration_config: 'Calibration Config',
  calibration_reply: 'Calibration Reply',
  acknowledgement: 'Acknowledgement',
  checkin: 'Check-in',
  checkin_cycle_batch: 'Check-in Batch',
  checkin_file: 'Check-in File',
  competency: 'Competency',
  cycle_goal: 'Cycle Goal',
  element: 'Element',
  goal_library: 'Goal Library',
  goal_template: 'Goal Template',
  goal_type: 'Goal Type',
  nominator: 'Nominator',
  nominee: 'Nominee',
  okr_check_in: 'OKR Check-in',
  okr_settings: 'OKR Settings',
  pip: 'Performance Plan',
  pip_goal: 'Performance Plan Goal',
  pip_objective: 'Performance Plan Objective',
  recurring_checkin_item: 'Recurring Check-in',
  survey: 'Survey',
  survey_participant: 'Survey Participant',
  survey_question: 'Survey Question',
  survey_question_label: 'Question Label',
  survey_question_type: 'Question Type',
  survey_template: 'Survey Template',
  survey_template_question: 'Template Question',
  survey_template_section: 'Template Section',
  survey_template_type: 'Template Type',
  survey_visibility_rule: 'Visibility Rule',
  template: 'Review Template',
  template_question: 'Template Question',
};

/** Human-readable display names for field names */
export const FIELD_DISPLAY_NAMES: Record<string, string> = {
  // Internal / meta fields
  org_id: 'Organization',
  account_id: 'Account',
  user_id: 'User',
  employee_id: 'Employee',
  created_by: 'Created By',
  updated_by: 'Updated By',
  created_at: 'Created At',
  updated_at: 'Updated At',
  is_active: 'Active',
  is_deleted: 'Deleted',
  // Common fields
  title: 'Title',
  description: 'Description',
  type: 'Type',
  name: 'Name',
  email: 'Email',
  phone: 'Phone',
  status: 'Status',
  position: 'Position',
  start_date: 'Start Date',
  end_date: 'End Date',
  // Employee fields
  first_name: 'First Name',
  last_name: 'Last Name',
  middle_name: 'Middle Name',
  dob: 'Date of Birth',
  date_of_birth: 'Date of Birth',
  gender: 'Gender',
  race: 'Ethnicity',
  marital_status: 'Marital Status',
  phone_number: 'Phone Number',
  phone_extension: 'Phone Extension',
  work_email: 'Work Email',
  personal_email: 'Personal Email',
  designation: 'Designation',
  department: 'Department',
  location: 'Location',
  location_id: 'Location',
  reporting_manager: 'Reporting Manager',
  manager: 'Manager',
  secondary_manager: 'Secondary Manager',
  hr_partner: 'HR Partner',
  date_of_joining: 'Date of Joining',
  hire_date: 'Hire Date',
  position_date: 'Position Date',
  employment_type: 'Employment Type',
  employment_status: 'Employment Status',
  job_title: 'Job Title',
  job_grade: 'Job Grade',
  job_code: 'Job Code',
  eeoc_category: 'EEOC Category',
  reporting_to_title: 'Reports To',
  work_role: 'Work Role',
  trainer_contact: 'Trainer Contact',
  standard_hr_per_week: 'Standard Hours Per Week',
  position_description: 'Position Description',
  system_access_enabled: 'System Access',
  is_hrmanager: 'HR Manager',
  entity_id: 'Organization Unit',
  division_id: 'Division',
  department_id: 'Department',
  termination_date: 'Termination Date',
  termination_reason: 'Termination Reason',
  direct_reports_reassigned: 'Direct Reports Reassigned',
  secondary_manager_cleared: 'Secondary Manager Cleared',
  hr_partner_cleared: 'HR Partner Cleared',
  unit_head_positions_reassigned: 'Unit Head Positions Reassigned',
  unit_head_positions_vacated: 'Unit Head Positions Vacated',
  unit_hr_positions_vacated: 'Unit HR Positions Vacated',
  employee_code: 'Employee Code',
  // Education
  school_name: 'School Name',
  level: 'Level',
  field_of_study: 'Field of Study',
  year: 'Year',
  // Experience
  company_name: 'Company Name',
  total_years_experience: 'Total Years of Experience',
  // Role & Permission fields
  role_name: 'Role Name',
  role_id: 'Role ID',
  permission_name: 'Permission',
  permission_id: 'Permission',
  scope_name: 'Name',
  parent_id: 'Parent',
  module_id: 'Module',
  role_type_id: 'Role Type',
  include_descendants: 'Include Sub-Units',
  scope_instance_id: 'Organization Unit',
  // Scope instance
  head_user_id: 'Unit Head',
  hr_head_user_id: 'HR Head',
  scope_count: 'Number of Units',
  instance_code: 'Unit Code',
  scope_id: 'Scope Level',
  parent_instance_id: 'Parent Unit',
  // Account settings
  min_age: 'Minimum Age',
  timezone: 'Timezone',
  send_welcome_email: 'Send Welcome Email',
  deleted: 'Records Deleted',
  company_id: 'Company ID',
  tax_id: 'Tax ID',
  naics_code: 'NAICS Code',
  fiscal_end: 'Fiscal Year End',
  client_id: 'Client ID',
  address1: 'Address',
  city: 'City',
  state: 'State',
  country: 'Country',
  zip_code: 'Zip Code',
  zip: 'Zip Code',
  zipcode: 'Zip Code',
  // Access method
  access_by: 'Access Method',
  login_attempts_limit: 'Login Attempts Limit',
  is_mfa_mandatory: 'MFA Required',
  is_sso_mandatory: 'SSO Required',
  otp_preference: 'OTP Preference',
  // Feature flags
  is_calibration_enabled: 'Calibration Enabled',
  is_approvers_enabled: 'Approvers Enabled',
  can_manage_customers: 'Customer Management',
  is_manager_override_score_enabled: 'Manager Override Score',
  is_overall_summary_enabled: 'Overall Summary',
  customer_label: 'Customer Label',
  account_type: 'Account Type',
  // Impersonation
  reason: 'Reason',
  duration_minutes: 'Duration (Minutes)',
  target_user_id: 'Target User',
  target_account_id: 'Target Account',
  session_token: 'Session',
  session_id: 'Session',
  // Module access
  is_enabled: 'Enabled',
  submodule_count: 'Number of Submodules',
  success_count: 'Successful',
  failed_count: 'Failed',
  count: 'Count',
  // Customer fields
  customer_type: 'Customer Type',
  domain: 'Domain',
  access: 'Module Access',
  portal_access: 'Portal Access',
  industry: 'Industry',
  enrollment_type: 'Enrollment Type',
  customer_code: 'Customer Code',
  // Location fields
  address: 'Address',
  // Workrole fields
  job_category: 'Job Category',
  job_group: 'Job Group',
  // Custom field fields
  field_name: 'Field Name',
  entity_type: 'Entity Type',
  custom_field_id: 'Custom Field',
  value_id: 'Value',
  value_updated: 'Value Updated',
  // Domain settings
  domain_check: 'Allow All Domains',
  // Attachment
  filename: 'File Name',
  s3_key: 'Storage Path',
  content_type: 'File Type',
  // Learning plan
  course_id: 'Course',
  participant_count: 'Participants',
  skipped_count: 'Skipped',
  mode: 'Mode',
  language: 'Language',
  category: 'Category',
  due_date: 'Due Date',
  certificate_url: 'Certificate',
  // Cycle fields
  cycle_title: 'Cycle Title',
  cycle_description: 'Cycle Description',
  cycle_type: 'Cycle Type',
  cycle_start_date: 'Start Date',
  cycle_end_date: 'End Date',
  template_id: 'Template',
  // Goal fields
  goal_title: 'Goal Title',
  goal_description: 'Goal Description',
  goal_type: 'Goal Type',
  goal_status: 'Status',
  weightage: 'Weightage',
  progress: 'Progress',
  // Review fields
  rating: 'Rating',
  overall_rating: 'Overall Rating',
  comments: 'Comments',
  feedback: 'Feedback',
  final_rating: 'Final Rating',
  manager_rating: 'Manager Rating',
  hr_rating: 'HR Rating',
  // Competency fields
  competency_name: 'Competency Name',
  competency_description: 'Description',
  // Check-in fields
  checkin_status: 'Status',
  checkin_date: 'Check-in Date',
  // Survey fields
  survey_title: 'Survey Title',
  survey_description: 'Survey Description',
  // PIP fields
  pip_status: 'Status',
  pip_start_date: 'Start Date',
  pip_end_date: 'End Date',
  // Template fields
  template_name: 'Template Name',
  template_description: 'Template Description',
};

/** Human-readable display names for action types */
export const ACTION_DISPLAY_NAMES: Record<string, string> = {
  CREATE: 'Created',
  UPDATE: 'Updated',
  DELETE: 'Deleted',
  DEACTIVATE: 'Deactivated',
  REACTIVATE: 'Reactivated',
  TERMINATE: 'Terminated',
  REASSIGN_DIRECT_REPORTS: 'Reassigned Reports',
  BULK_IMPORT: 'Bulk Imported',
  BULK_CREATE: 'Bulk Created',
  SUBMIT_EMPLOYEE_REVIEW: 'Submitted Employee Review',
  SUBMIT_MANAGER_REVIEW: 'Submitted Manager Review',
  SUBMIT_HR_REVIEW: 'Submitted HR Review',
  SUBMIT_EMPLOYEE_CHECKIN: 'Submitted Employee Check-in',
  SUBMIT_MANAGER_CHECKIN: 'Submitted Manager Check-in',
  SUBMIT_HR_CHECKIN: 'Submitted HR Check-in',
  UNAPPROVE_MANAGER: 'Manager Unapproved',
  UNAPPROVE_EMPLOYEE: 'Employee Unapproved',
  INITIAL_REVIEW_COMPLETE: 'Initial Review Completed',
  CLONE: 'Cloned',
  ACTIVATE: 'Activated',
  ARCHIVE: 'Archived',
  PUBLISH: 'Published',
  UNPUBLISH: 'Unpublished',
  ASSIGN: 'Assigned',
  UNASSIGN: 'Unassigned',
  APPROVE: 'Approved',
  REJECT: 'Rejected',
  SUBMIT: 'Submitted',
  COMPLETE: 'Completed',
  ADD: 'Added',
  REMOVE: 'Removed',
  UPDATE_HEAD: 'Updated Head',
  UPDATE_HR_HEAD: 'Updated HR Head',
  ADD_HR_HEAD: 'Added HR Head',
  REMOVE_HR_HEAD: 'Removed HR Head',
  START: 'Started',
  END: 'Ended',
  FORCE_END: 'Force Ended',
};

/** Fields that should be hidden from the audit changes table (internal IDs, etc.) */
const HIDDEN_FIELDS = new Set([
  'id',
  'org_id',
  'account_id',
  'user_id',
  'employee_id',
  'created_by',
  'updated_by',
  'created_at',
  'updated_at',
  'is_deleted',
  'customer_employee_id',
  'template_id',
  'parent_id',
  'cycle_id',
  'goal_id',
  'review_id',
  'survey_id',
  'calibration_id',
  'flowboard_id',
  'grid_cycle_id',
  'pip_id',
]);

/** Get human-readable entity type name */
export function getEntityDisplayName(entityType: string): string {
  return ENTITY_DISPLAY_NAMES[entityType] || entityType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/** Common abbreviations that should stay uppercase in field labels */
const ABBREVIATIONS: Record<string, string> = {
  hr: 'HR', id: 'ID', sso: 'SSO', mfa: 'MFA', otp: 'OTP', url: 'URL',
  ip: 'IP', eeoc: 'EEOC', naics: 'NAICS', lms: 'LMS', tms: 'TMS',
  okr: 'OKR', pip: 'PIP', idp: 'IDP', uuid: 'UUID', s3: 'S3',
};

/** Get human-readable field name */
export function getFieldDisplayName(field: string): string {
  if (FIELD_DISPLAY_NAMES[field]) return FIELD_DISPLAY_NAMES[field];
  // Remove is_ prefix for boolean fields and label them naturally
  const cleanField = field.replace(/^is_/, '');
  return cleanField
    .split('_')
    .map(word => ABBREVIATIONS[word.toLowerCase()] || word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/** Get human-readable action description */
export function getActionDisplayName(action: string): string {
  return ACTION_DISPLAY_NAMES[action] || action.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

/** Check if a field should be hidden from the changes table */
export function isHiddenField(field: string): boolean {
  return HIDDEN_FIELDS.has(field);
}

/** Filter out hidden fields from changes array */
export function filterVisibleChanges(changes: Array<{ field: string; from?: any; to?: any }>): Array<{ field: string; from?: any; to?: any }> {
  return changes.filter(change => !isHiddenField(change.field));
}

/** Format a value for display - handles dates, booleans, nulls */
export function formatDisplayValue(value: any, fieldName?: string): string {
  if (value === undefined || value === null || value === '' || value === '-') return '-';

  // Boolean display (native boolean)
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  if (typeof value === 'string') {
    // Boolean strings from backend
    if (value === 'true') return 'Yes';
    if (value === 'false') return 'No';

    // ISO date pattern
    if (/^\d{4}-\d{2}-\d{2}(T|$)/.test(value)) {
      if (value.startsWith('0001-01-01')) return 'Not Set';
      try {
        const d = new Date(value);
        if (!isNaN(d.getTime())) {
          return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        }
      } catch { /* fall through */ }
    }
  }

  // Large numeric IDs (> 10 digits) — likely snowflake IDs, not useful to show
  if (typeof value === 'number' && Math.abs(value) > 9999999999) return '-';

  if (typeof value === 'object') {
    if (Array.isArray(value)) return value.join(', ');
    return JSON.stringify(value);
  }
  return String(value);
}
